import React, { useState, useCallback } from 'react';
import { useScoring } from '../../contexts/ScoringContext';
import { 
  ScoringWeights, 
  METRIC_LABELS, 
  METRIC_DESCRIPTIONS,
  normalizeWeights 
} from '../../config/scoringConfig';
import { UI_THEME } from '../../config/constants';

// Modal styling constants
const MODAL_STYLES = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(4px)'
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: UI_THEME.borderRadius.md,
    maxWidth: '480px',
    width: '90%',
    maxHeight: '75vh',
    overflow: 'hidden' as const,
    boxShadow: UI_THEME.shadows.glass
  },
  header: {
    background: 'linear-gradient(to right, #0ea5e9, #0369a1, #a855f7)',
    color: 'white',
    padding: UI_THEME.spacing.lg,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }
} as const;

interface ScoringSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ScoringSettingsModal: React.FC<ScoringSettingsModalProps> = ({ isOpen, onClose }) => {
  const { weights, updateWeights, resetToDefaults, isCustomScoring } = useScoring();
  const [localWeights, setLocalWeights] = useState<ScoringWeights>(weights);
  const [hasChanges, setHasChanges] = useState(false);

  // Sync local weights when modal opens or external weights change
  React.useEffect(() => {
    setLocalWeights(weights);
    setHasChanges(false);
  }, [weights, isOpen]);

  const handleSliderChange = useCallback((metric: keyof ScoringWeights, value: number) => {
    const newWeights = { ...localWeights, [metric]: value };
    const normalized = normalizeWeights(newWeights);
    setLocalWeights(normalized);
    setHasChanges(true);
  }, [localWeights]);

  const handleSave = () => {
    updateWeights(localWeights);
    setHasChanges(false);
    onClose();
  };

  const handleCancel = () => {
    setLocalWeights(weights);
    setHasChanges(false);
    onClose();
  };

  const handleResetToDefaults = () => {
    resetToDefaults();
    setHasChanges(false);
  };

  if (!isOpen) return null;

  const weightEntries = Object.entries(localWeights) as [keyof ScoringWeights, number][];

  return (
    <div style={MODAL_STYLES.overlay}>
      <div style={MODAL_STYLES.modal}>
        {/* Header */}
        <div style={MODAL_STYLES.header}>
          <h2 style={{ margin: 0, fontSize: UI_THEME.fontSize.lg, fontWeight: UI_THEME.fontWeight.bold }}>
            Lead Scoring Configuration
          </h2>
          <button
            onClick={handleCancel}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: 'white',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ 
          padding: `${UI_THEME.spacing.sm} ${UI_THEME.spacing.md}`,
          maxHeight: 'calc(75vh - 140px)',
          overflowY: 'auto'
        }}>
          {/* Weight Sliders */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: UI_THEME.spacing.sm }}>
            {weightEntries.map(([metric, weight]) => (
              <div key={metric} style={{ marginBottom: '2px' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '2px'
                }}>
                  <label style={{ 
                    fontSize: '12px',
                    fontWeight: UI_THEME.fontWeight.medium,
                    color: UI_THEME.colors.gray[700]
                  }}>
                    {METRIC_LABELS[metric]}
                  </label>
                  <span style={{ 
                    fontSize: '12px',
                    color: UI_THEME.colors.primary,
                    fontWeight: UI_THEME.fontWeight.medium,
                    minWidth: '40px',
                    textAlign: 'right'
                  }}>
                    {(weight * 100).toFixed(1)}%
                  </span>
                </div>
                
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={weight}
                  onChange={(e) => handleSliderChange(metric, parseFloat(e.target.value))}
                  style={{
                    width: '100%',
                    height: '8px',
                    borderRadius: '4px',
                    outline: 'none',
                    WebkitAppearance: 'none',
                    background: `linear-gradient(to right, ${UI_THEME.colors.primary} 0%, ${UI_THEME.colors.primary} ${weight * 100}%, ${UI_THEME.colors.gray[200]} ${weight * 100}%, ${UI_THEME.colors.gray[200]} 100%)`,
                    cursor: 'pointer'
                  }}
                />
                
                <p style={{ 
                  fontSize: '10px',
                  color: UI_THEME.colors.gray[500],
                  margin: '1px 0 0 0',
                  lineHeight: '1.2'
                }}>
                  {METRIC_DESCRIPTIONS[metric]}
                </p>
              </div>
            ))}
          </div>

          {/* Status Indicator */}
          {isCustomScoring && (
            <div style={{
              backgroundColor: UI_THEME.colors.gray[100],
              border: `1px solid ${UI_THEME.colors.gray[300]}`,
              borderRadius: UI_THEME.borderRadius.sm,
              padding: `${UI_THEME.spacing.xs} ${UI_THEME.spacing.sm}`,
              marginTop: UI_THEME.spacing.sm,
              display: 'flex',
              alignItems: 'center',
              gap: UI_THEME.spacing.xs
            }}>
              <span style={{ color: UI_THEME.colors.primary, fontSize: '12px' }}>⚡</span>
              <span style={{ fontSize: '12px', color: UI_THEME.colors.gray[700] }}>
                Custom scoring is active
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          borderTop: `1px solid ${UI_THEME.colors.gray[200]}`,
          padding: `${UI_THEME.spacing.sm} ${UI_THEME.spacing.md}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: UI_THEME.spacing.sm
        }}>
          <button
            onClick={handleResetToDefaults}
            style={{
              padding: `${UI_THEME.spacing.sm} ${UI_THEME.spacing.md}`,
              backgroundColor: UI_THEME.colors.purple[100],
              color: UI_THEME.colors.purple[700],
              border: `1px solid ${UI_THEME.colors.purple[200]}`,
              borderRadius: UI_THEME.borderRadius.sm,
              cursor: 'pointer',
              fontSize: UI_THEME.fontSize.sm,
              fontWeight: UI_THEME.fontWeight.medium,
              transition: 'all 0.2s ease',
              boxShadow: '0 1px 2px rgba(168, 85, 247, 0.1)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = UI_THEME.colors.purple[200];
              e.currentTarget.style.borderColor = UI_THEME.colors.purple[300];
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(168, 85, 247, 0.15)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = UI_THEME.colors.purple[100];
              e.currentTarget.style.borderColor = UI_THEME.colors.purple[200];
              e.currentTarget.style.transform = 'translateY(0px)';
              e.currentTarget.style.boxShadow = '0 1px 2px rgba(168, 85, 247, 0.1)';
            }}
          >
            Reset to Defaults
          </button>

          <div style={{ display: 'flex', gap: UI_THEME.spacing.sm }}>
            <button
              onClick={handleCancel}
              style={{
                padding: `${UI_THEME.spacing.sm} ${UI_THEME.spacing.md}`,
                backgroundColor: 'white',
                color: UI_THEME.colors.gray[600],
                border: `1px solid ${UI_THEME.colors.gray[300]}`,
                borderRadius: UI_THEME.borderRadius.sm,
                cursor: 'pointer',
                fontSize: UI_THEME.fontSize.sm,
                fontWeight: UI_THEME.fontWeight.medium,
                transition: 'all 0.2s ease',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = UI_THEME.colors.gray[100];
                e.currentTarget.style.borderColor = UI_THEME.colors.gray[400];
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.borderColor = UI_THEME.colors.gray[300];
              }}
            >
              Cancel
            </button>
            
            <button
              onClick={handleSave}
              disabled={!hasChanges}
              style={{
                padding: `${UI_THEME.spacing.sm} ${UI_THEME.spacing.md}`,
                backgroundColor: hasChanges ? UI_THEME.colors.primary : UI_THEME.colors.gray[300],
                color: hasChanges ? 'white' : UI_THEME.colors.gray[500],
                border: hasChanges ? `1px solid ${UI_THEME.colors.primary}` : `1px solid ${UI_THEME.colors.gray[300]}`,
                borderRadius: UI_THEME.borderRadius.sm,
                cursor: hasChanges ? 'pointer' : 'not-allowed',
                fontSize: UI_THEME.fontSize.sm,
                fontWeight: UI_THEME.fontWeight.medium,
                transition: 'all 0.2s ease',
                boxShadow: hasChanges ? '0 2px 4px rgba(14, 165, 233, 0.2)' : '0 1px 2px rgba(0, 0, 0, 0.05)',
                opacity: hasChanges ? 1 : 0.6
              }}
              onMouseOver={(e) => {
                if (hasChanges) {
                  e.currentTarget.style.backgroundColor = UI_THEME.colors.primaryHover;
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(14, 165, 233, 0.25)';
                }
              }}
              onMouseOut={(e) => {
                if (hasChanges) {
                  e.currentTarget.style.backgroundColor = UI_THEME.colors.primary;
                  e.currentTarget.style.transform = 'translateY(0px)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(14, 165, 233, 0.2)';
                }
              }}
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScoringSettingsModal;